// src/components/OfflineBanner.js
//
// The crew's window into offline mode. Three states, in priority order:
//
//   failed > 0     something couldn't sync and needs a person — always loudest,
//                  because silently dropping a crew's work is the one outcome
//                  this whole feature exists to prevent.
//   needsLogin     queue is held because the session expired (usually a backend
//                  restart). Their work is safe; they just have to sign back in.
//   offline/pending  normal "working offline, N items waiting" state.
//
// Renders nothing when online with an empty queue, so office staff never see it.

import React, { useState } from 'react';
import { FiWifiOff, FiUploadCloud, FiAlertTriangle, FiCheck, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useOffline } from '../hooks/useOffline';

const KIND_LABEL = {
  'clock-in': 'Clock in',
  'clock-in-crew': 'Crew clock in',
  'clock-out': 'Clock out',
  'clock-out-job': 'Crew clock out',
  break: 'Break',
  timer: 'Timer',
  'timer-crew': 'Crew timer',
  note: 'Job note',
  photo: 'Photo',
  checklist: 'Field checklist',
  'checklist-note': 'Checklist note',
};

function timeAgo(ms) {
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function OfflineBanner() {
  const { online, pending, failed, pendingCount, failedCount, needsLogin, syncNow, retryOp, discardOp } = useOffline();
  const [expanded, setExpanded] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const nothingToShow = online && pendingCount === 0 && failedCount === 0;
  if (nothingToShow) return null;

  const handleSync = async () => {
    setSyncing(true);
    await syncNow();
    // Brief hold so the button doesn't flicker on a fast drain.
    setTimeout(() => setSyncing(false), 600);
  };

  let bg = '#fef3c7';
  let border = '#f59e0b';
  let fg = '#92400e';
  let icon = <FiWifiOff size={16} />;
  let message = 'Working offline — your work is being saved on this device.';

  if (failedCount > 0) {
    bg = '#fee2e2';
    border = '#dc2626';
    fg = '#991b1b';
    icon = <FiAlertTriangle size={16} />;
    message = `${failedCount} item${failedCount === 1 ? '' : 's'} couldn't sync and need${failedCount === 1 ? 's' : ''} your attention.`;
  } else if (needsLogin) {
    bg = '#dbeafe';
    border = '#2563eb';
    fg = '#1e40af';
    icon = <FiAlertTriangle size={16} />;
    message = `Signed out with ${pendingCount} item${pendingCount === 1 ? '' : 's'} waiting. Log back in to finish syncing — nothing is lost.`;
  } else if (online && pendingCount > 0) {
    bg = '#dbeafe';
    border = '#2563eb';
    fg = '#1e40af';
    icon = <FiUploadCloud size={16} />;
    message = `Syncing ${pendingCount} item${pendingCount === 1 ? '' : 's'}…`;
  } else if (!online && pendingCount > 0) {
    message = `Working offline — ${pendingCount} item${pendingCount === 1 ? '' : 's'} saved on this device, will sync automatically.`;
  }

  const rows = [...failed, ...pending];

  return (
    <div style={{ backgroundColor: bg, borderBottom: `2px solid ${border}`, zIndex: 998 }}>
      <div
        onClick={() => rows.length > 0 && setExpanded(!expanded)}
        style={{
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          cursor: rows.length > 0 ? 'pointer' : 'default',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ color: fg, display: 'flex', alignItems: 'center' }}>{icon}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: fg }}>{message}</span>

        {online && pendingCount > 0 && !needsLogin && (
          <button
            onClick={(e) => { e.stopPropagation(); handleSync(); }}
            disabled={syncing}
            style={{
              background: 'transparent',
              border: `1px solid ${border}`,
              color: fg,
              borderRadius: 6,
              padding: '3px 10px',
              fontSize: 12,
              fontWeight: 600,
              cursor: syncing ? 'default' : 'pointer',
            }}
          >
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        )}

        {rows.length > 0 && (
          <span style={{ color: fg, display: 'flex', alignItems: 'center' }}>
            {expanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
          </span>
        )}
      </div>

      {expanded && rows.length > 0 && (
        <div style={{ padding: '0 16px 12px', maxHeight: 260, overflowY: 'auto' }}>
          {rows.map((op) => {
            const isFailed = op.status === 'failed';
            return (
              <div
                key={op.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  marginBottom: 6,
                  background: 'rgba(255,255,255,0.7)',
                  borderRadius: 8,
                  borderLeft: `3px solid ${isFailed ? '#dc2626' : border}`,
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', display: 'block' }}>
                    {KIND_LABEL[op.kind] || op.label}
                    {op.jobId ? ` — job #${op.jobId}` : ''}
                  </span>
                  <span style={{ fontSize: 11, color: isFailed ? '#991b1b' : '#6b7280' }}>
                    {isFailed ? op.lastError || 'Sync failed' : timeAgo(op.createdAt)}
                  </span>
                </span>

                {isFailed ? (
                  <>
                    <button
                      onClick={() => retryOp(op.id)}
                      style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Try again
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Discard this item? It will not be saved anywhere.')) discardOp(op.id);
                      }}
                      style={{ background: 'transparent', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
                    >
                      Discard
                    </button>
                  </>
                ) : (
                  <span style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FiCheck size={12} /> Saved
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
